import { type ClassValue, cx } from 'cva';
import type ExcelJS from 'exceljs';
import { twMerge } from 'tailwind-merge';
import { tryCatchSync } from '@/try-catch';

export const cn = (...inputs: ClassValue[]) => twMerge(cx(inputs));

export const formatConstant = (value: string) => {
  const text = value.replaceAll('_', ' ').toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const getColorFromId = (id: string): string => {
  const AVATAR_COLORS = [
    'bg-indigo-100 text-indigo-900',
    'bg-violet-100 text-violet-900',
    'bg-pink-100 text-pink-900',
    'bg-rose-100 text-rose-900',
    'bg-amber-100 text-amber-900',
    'bg-emerald-100 text-emerald-900',
    'bg-teal-100 text-teal-900',
    'bg-cyan-100 text-cyan-900',
    'bg-blue-100 text-blue-900',
    'bg-purple-100 text-purple-900',
  ];

  if (id === '') return 'bg-surface-9';
  const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length] ?? 'bg-surface-9';
};

export const formatDuration = (seconds: number) => {
  const whole = Math.max(0, Math.floor(seconds));
  if (whole < 60) return `${whole}s`;
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
};

export const elapsedSeconds = (
  startedAt: string | null | undefined,
  endedAt: string | number | null | undefined,
): number | null => {
  const start = Date.parse(startedAt ?? '');
  const end = typeof endedAt === 'number' ? endedAt : Date.parse(endedAt ?? '');
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  return Math.max(0, Math.floor((end - start) / 1000));
};

export const stripMarkdown = (md: string): string =>
  md
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s*>+\s?/gm, '')
    .replace(/^\s*([-*+]|\d+\.)\s+/gm, '')
    .replace(/^\s*([-*_])(\s*\1){2,}\s*$/gm, '')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/~~([^~]*)~~/g, '$1')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

export const openFilePicker = ({
  accept,
  multiple = false,
  onFiles,
}: {
  accept?: string;
  multiple?: boolean;
  onFiles: (files: File[]) => void;
}) => {
  const input = document.createElement('input');
  input.type = 'file';
  if (accept) input.accept = accept;
  input.multiple = multiple;
  input.onchange = () => {
    if (input.files?.length) onFiles(Array.from(input.files));
  };
  input.click();
};

export const downloadBlob = (blob: Blob, filename: string) => {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
};

export const filenameFromContentDisposition = (disposition: unknown, fallback: string) => {
  const header = typeof disposition === 'string' ? disposition : '';
  const quoted = /filename="([^"]*)"/i.exec(header);
  const plain = (quoted ? quoted[1] : /filename=([^;]+)/i.exec(header)?.[1]?.trim()) || fallback;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header)?.[1];
  if (!encoded) return plain;
  return tryCatchSync(() => decodeURIComponent(encoded)).data ?? plain;
};

export const parseExportWarnings = (raw: unknown): string[] => {
  if (typeof raw !== 'string' || raw.length === 0) return [];
  return tryCatchSync(() => JSON.parse(raw) as string[]).data ?? [];
};

const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const WORKSHEET_NAME_MAX_LENGTH = 31;
const RESERVED_WORKSHEET_NAME = 'history';
const FORBIDDEN_WORKSHEET_CHARACTERS = /[\\/?*[\]:]/g;
const WORKSHEET_NAME_EDGES = /^[\s']+|[\s']+$/g;

const CELL_BORDER: Partial<ExcelJS.Borders> = {
  top: { style: 'thin' },
  left: { style: 'thin' },
  bottom: { style: 'thin' },
  right: { style: 'thin' },
};

export const addHeaderRow = (worksheet: ExcelJS.Worksheet, headers: string[]) => {
  worksheet.addRow(headers).eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
    cell.border = CELL_BORDER;
  });
};

export const styleDataRow = (row: ExcelJS.Row) => {
  row.eachCell((cell) => {
    cell.border = CELL_BORDER;
    cell.alignment = { vertical: 'top', wrapText: true };
  });
};

export const sanitizeFilename = (name: string) => name.replace(/[^a-zA-Z0-9 ]/g, '_');

export const saveWorkbook = async (workbook: ExcelJS.Workbook, fileName: string) => {
  const buffer = await workbook.xlsx.writeBuffer();
  downloadBlob(new Blob([buffer], { type: XLSX_MIME_TYPE }), `${fileName}.xlsx`);
};

export const addUniqueWorksheet = (workbook: ExcelJS.Workbook, name: string, fallback: string) => {
  const base =
    name
      .replace(FORBIDDEN_WORKSHEET_CHARACTERS, ' ')
      .trim()
      .slice(0, WORKSHEET_NAME_MAX_LENGTH)
      .replace(WORKSHEET_NAME_EDGES, '') || fallback;
  const taken = new Set([
    RESERVED_WORKSHEET_NAME,
    ...workbook.worksheets.map((worksheet) => worksheet.name.toLowerCase()),
  ]);
  let unique = base;
  for (let copy = 2; taken.has(unique.toLowerCase()); copy++) {
    const suffix = ` (${copy})`;
    unique = `${base.slice(0, WORKSHEET_NAME_MAX_LENGTH - suffix.length)}${suffix}`;
  }
  return workbook.addWorksheet(unique);
};
